<script>
  import { onMount } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import ParticipantDetails from '$lib/ParticipantDetails.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { config, delayHours } from '$stores/configStore';
  import { glucoseunit } from '$stores/unitsStore';
  import { convertGlucoseValue, formatGlucoseValue } from '$utils/unitUtils';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faRotate } from '@fortawesome/pro-light-svg-icons';
  
  // Use local API endpoints instead of direct API with key
  const API_BASE_URL = '/api/proxy';
  
  const demoMode = import.meta.env.VITE_DEMO_MODE === 'true';

  import { formatToLocalDateTime } from '$utils/dateUtils.js';
  import { exportToExcel, generateExportFilename } from '$utils/export.js';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import StudyDetails from '$lib/StudyDetails.svelte';
  import SiteDetails from '$lib/SiteDetails.svelte';

  let readings = [];
  let resultCount = 0;
  let currentPage = 1;
  let totalPages = 1;
  let limit = 20;
  let isLoading = true;
  let isExporting = false;
  let inRange = true;
  onMount(async () => {
    if ($selectedStudy) {
      fetchReadings(1);
    }
  });

  // Subscribe to selectedStudy changes
  $: {
    if ($selectedStudy) {
      fetchReadings(1);
    }
  }

  async function fetchReadings(page = 1) {
    try {
      isLoading = true;
      let url = `${API_BASE_URL}/glucoseReadings?page=${page}&pageSize=${limit}`;

      url += `&type=CGM`;
      url += `&inRange=${inRange}`;

      if ($selectedParticipant) {
        url += `&subjectGUID=${$selectedParticipant._id}`;
      }

      if ($selectedSite) {
        url += `&siteGUID=${$selectedSite._id}`;
      }

      if ($selectedStudy) {
        url += `&studyGUID=${$selectedStudy._id}`;
      }

      if ($delayHours > 0) {
        url += `&delayHours=${$delayHours}`;
      }

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch glucose readings');
      }

      const responseData = await res.json();

      resultCount = responseData.totalCount || 0;
      currentPage = responseData.page || 1;
      totalPages = responseData.totalPages || 1;

      readings = responseData.data;
    } catch (err) {
      console.error('Error fetching glucose readings:', err);
      readings = [];
    } finally {
      isLoading = false;
    }
  }

  function handleRefresh() {
    fetchReadings(1);
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      fetchReadings(newPage);
    }
  }

  async function fetchAllReadings() {
    try {
      let url = `${API_BASE_URL}/glucoseReadings?page=1&pageSize=999999`;

      url += `&type=CGM`;
      url += `&inRange=${inRange}`;

      if ($selectedParticipant) {
        url += `&subjectGUID=${$selectedParticipant._id}`;
      }

      if ($selectedSite) {
        url += `&siteGUID=${$selectedSite._id}`;
      }

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch all glucose readings');
      }

      const result = await res.json();
      return result.data || [];
    } catch (err) {
      console.error('Error fetching all glucose readings:', err);
      return [];
    }
  }

  async function handleExportExcel() {
    try {
      isExporting = true;
      const allReadings = await fetchAllReadings();

      const now = new Date();
      const dateStr = now
        .toLocaleString('en-US', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        })
        .replace(/,/g, '')
        .replace(/\s+/g, '')
        .replace(/:/g, '')
        .replace(/(\d{2})([A-Za-z]{3})(\d{4})_(\d{2})(\d{2})/, '$2$1$3_$4$5');

      const filename = generateExportFilename({
        protocolNumber: $selectedStudy?.protocolNumber,
        subjectId: $selectedParticipant?.subjectId,
        reportType: 'CGM_Readings'
      });

      const formattedData = allReadings.map((item) => ({
        Subject: $selectedParticipant.subjectId || '',
        Manufacturer: item.manufacturer || '',
        Model: item.model || '',
        Sensor: item.externalId || '',
        Type: item.type || '',
        Value: item.value != null ? convertGlucoseValue(item.value, $glucoseunit) : '',
        Unit: $glucoseunit,
        Backfilled: isBackfilledReading(item.raw) ? 'yes' : '',
        'Reading Time (Local)': item.read ? formatToLocalDateTime(item.read, item.timezone) : '',
        'Received Time (Local)': item.received
          ? formatToLocalDateTime(item.received, item.timezone)
          : '',
        Timezone: item.timezone || '',
        'Reading Time (UTC)': item.read ? formatToLocalDateTime(item.read, 'UTC') : '',
        'Received Time (UTC)': item.received ? formatToLocalDateTime(item.received, 'UTC') : '',
        'Error Code': item.value === 0 ? 'invalid' : (item.raw ? formatDqCode(item.raw) : ''),
      }));

      await exportToExcel(formattedData, filename);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    } finally {
      isExporting = false;
    }
  }

  function isBackfilledReading(raw) {
    if (!raw || typeof raw !== 'string') return false;
    return raw.includes('"isBackfilled":true') || raw.includes('"wasBackfilled":true');
  }

  function formatDqCode(rawString) {
    try {
      const rawData = JSON.parse(rawString);
      return (
        rawData.dqCode
          ?.replace(/[\[\]]/g, '')
          .split(',')
          .join(', ') ?? ''
      );
    } catch {
      return '';
    }
  }
</script>

<!-- Add loading overlay for export -->
{#if isExporting}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg flex flex-col items-center">
      <div class="loader mb-4"></div>
      <p class="text-sm text-gray-700 dark:text-gray-300">Preparing Excel export...</p>
    </div>
  </div>
{/if}

<StudyHeader />

<FeatureInfo 
  title="CGM Readings Report" 
  description="This page displays detailed CGM readings for participants in the study. You can view sensor information, glucose values, 
  and reading timestamps. Use the Excel export function to download the complete dataset for further analysis. CGM exports will contain timestamps in the participant's local timezone and UTC. 
  The delay setting defines if the readings are delayed by a certain number of hours (3h for Europe)."
  marginBottom="mb-0"
/>

{#if $selectedParticipant}
  <ParticipantDetails showOptionsMenu={false} />
{:else if $selectedSite}
  <SiteDetails showOptionsMenu={false} />
{:else if $selectedStudy}
  <StudyDetails showOptionsMenu={false} />
{/if}

<!-- Title and Filters -->
<Toolbar title="CGM Readings Report" itemCountTitle="Measurements" itemCount={resultCount}>
  <div class="text-m flex items-center gap-2">
    {#if $config.delayVisible}
      <div class="flex items-center mx-2 gap-1">
        Delay
        <select
          bind:value={$delayHours}
          on:change={() => handleRefresh()}
          class="form-select h-6 w-24 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
        >
          <option value={0}>None</option>
          <option value={3}>3 Hours</option>
        </select>
      </div>
    {:else if $delayHours > 0}
      <div class="flex items-center mx-2">
        <span class="me-1">Delay {$delayHours} hours</span>
      </div>
    {/if}
    <div class="flex items-center mx-2 print:hidden">
      <input
        type="checkbox"
        bind:checked={inRange}
        on:change={() => handleRefresh()}
        class="w-4 h-6 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 dark:focus:ring-red-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
      />
      <span class="ms-2">Valid Device Range</span>
    </div>
    <div class="flex items-center mx-2">
      <div class="flex items-baseline">
        <button on:click={() => handleRefresh()} class="pagination mx-1 p-0" title="Refresh">
          <FontAwesomeIcon icon={faRotate} />
        </button>
      </div>
    </div>
  </div>
</Toolbar>

{#if isLoading}
  <div class="flex flex-col justify-center items-center h-64 bg-stone-100/50 dark:bg-gray-725 mx-1">
    <div class="loader mb-4"></div>
    <p class="text-xs justify-center text-gray-700 dark:text-gray-300">
      preparing CGM readings report
    </p>
  </div>
{:else}
  <div class="px-1 pt-0">
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="px-4 py-2 text-left bold">Subject</th>
          <th class="px-4 py-2 text-left bold">Manufacturer</th>
          <th class="px-4 py-2 text-left bold">Model</th>
          <th class="px-4 py-2 text-left bold">Sensor</th>
          <th class="px-4 py-2 text-left bold">Type</th>
          <th class="px-4 py-2 text-left bold">Value</th>
          <th class="px-4 py-2 text-left bold">Unit</th>
          <th class="px-4 py-2 text-center bold">B?</th>
          <th class="px-4 py-2 text-left bold">Reading Time (Local)</th>
          <th class="px-4 py-2 text-left bold">Received Time (Local)</th>
          <th class="px-4 py-2 text-left bold">Timezone</th>
          <th class="px-4 py-2 text-left bold">Error Code</th>
        </tr>
      </thead>
      <tbody>
        {#each readings as reading, index}
          <tr
            class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
              2 ===
            0
              ? 'bg-stone-50 dark:bg-gray-725'
              : 'bg-stone-75 dark:bg-gray-750'}"
          >
            <td class="px-4 py-2">{$selectedParticipant.subjectId ?? 'n/a'}</td>
            <td class="px-4 py-2">{reading.manufacturer ?? 'n/a'}</td>
            <td class="px-4 py-2">{reading.model ?? 'n/a'}</td>
            <td class="px-4 py-2">{reading.externalId ?? 'n/a'}</td>
            <td class="px-4 py-2">{reading.type ?? 'n/a'}</td>
            <td class="px-4 py-2">{reading.value != null ? convertGlucoseValue(reading.value, $glucoseunit) : 'n/a'}</td>
            <td class="px-4 py-2">{$glucoseunit}</td>
            <td class="px-4 py-2 text-center"><span class="text-[0.6em]" style="color: {isBackfilledReading(reading.raw) ? 'rgba(50,130,170,0.75)' : 'rgba(60,170,170,0.75)'}">●</span></td>
            <td class="px-4 py-2"
              >{reading.read ? formatToLocalDateTime(reading.read, reading.timezone) : 'n/a'}</td
            >
            <td class="px-4 py-2"
              >{reading.received
                ? formatToLocalDateTime(reading.received, reading.timezone)
                : 'n/a'}</td
            >
            <td class="px-4 py-2">{reading.timezone ?? 'n/a'}</td>
            <td class="px-4 py-2 w-48">
              {#if reading.value === 0}
                invalid
              {:else if reading.raw}
                {formatDqCode(reading.raw)}
              {:else}
                n/a
              {/if}
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<!-- Add Control Buttons -->
<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
  <div>
    <button
      on:click={handleExportExcel}
      class="bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-4 rounded-full text-xs"
      >Excel</button
    >
  </div>
</Footer>

<style>
  .loader {
    border: 2px solid #20669c;
    border-radius: 50%;
    border-top: 4px solid #98acb4;
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .fixed {
    position: fixed;
  }

  .inset-0 {
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  .bg-opacity-50 {
    --tw-bg-opacity: 0.5;
  }

  .z-50 {
    z-index: 50;
  }

  .shadow-lg {
    --tw-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
    --tw-shadow-colored: 0 10px 15px -3px var(--tw-shadow-color),
      0 4px 6px -4px var(--tw-shadow-color);
    box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000),
      var(--tw-shadow);
  }
</style>
